import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { lightColors, themeBackgrounds } from '../../theme/theme';
import CreateChatBot from './CreateChatBot';
import UpdateChatBot from './UpdateChatBot';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface ChatBotItem {
  id: string;
  bot_id: string;
  bot_name: string;
  welcome_message: string;
  no_result_message: string;
  handover_message: string;
  status?: string;
  created_at?: string;
  avatarColor: string;
}

const ChatBot: React.FC = () => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBot, setSelectedBot] = useState<ChatBotItem | null>(null);
  const [bots, setBots] = useState<ChatBotItem[]>([]);
  const [isLoadingBots, setIsLoadingBots] = useState(true);

  const extractBots = (decrypted: any): any[] => {
    if (Array.isArray(decrypted)) return decrypted;
    if (Array.isArray(decrypted?.chat_bots)) return decrypted.chat_bots;
    if (Array.isArray(decrypted?.chatBots)) return decrypted.chatBots;
    if (Array.isArray(decrypted?.bots)) return decrypted.bots;
    if (Array.isArray(decrypted?.data)) return decrypted.data;
    return [];
  };

  const fetchChatBots = async (): Promise<void> => {
    const organizationId = userDetails?.organization_id || '';
    const applicationId = appId || '';

    if (!organizationId || !applicationId) {
      setBots([]);
      setIsLoadingBots(false);
      return;
    }

    setIsLoadingBots(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getApplicattionChatBots`,
        payload
      );

      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(
          response.data.data,
          getSessionKey() as string
        );
        const pageBots = extractBots(decrypted);

        const transformedBots: ChatBotItem[] = pageBots.map((bot: any, index: number) => ({
          id: bot._id || bot.bot_id || bot.id || `bot-${index}`,
          bot_id: bot.bot_id || bot._id || bot.id || `bot-${index}`,
          bot_name: bot.bot_name || bot.name || 'Untitled Bot',
          welcome_message: bot.welcome_message || '',
          no_result_message: bot.no_result_message || '',
          handover_message: bot.handover_message || '',
          status: bot.status || bot.bot_status || 'Active',
          created_at: bot.created_at,
          avatarColor: lightColors[index % lightColors.length],
        }));

        setBots(transformedBots);
      } else {
        setBots([]);
      }
    } catch (error) {
      console.log('Error fetching chat bots:', error);
      toast.error('Failed to load chat bots. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setBots([]);
    } finally {
      setIsLoadingBots(false);
    }
  };

  useEffect(()=>{
    setShowCreateForm(false);
    setSelectedBot(null);
  },[appId])

  useEffect(() => {
    if (!showCreateForm && !selectedBot) {
      fetchChatBots();
    }
  }, [userDetails?.organization_id, appId, showCreateForm, selectedBot]);

  const handleCreated = (): void => {
    setShowCreateForm(false);
  };

  if (showCreateForm) {
    return (
      <CreateChatBot
        onCancel={() => setShowCreateForm(false)}
        onCreated={handleCreated}
      />
    );
  }

  if (selectedBot) {
    return (
      <UpdateChatBot
        bot={selectedBot}
        onCancel={() => setSelectedBot(null)}
        onUpdated={() => setSelectedBot(null)}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <PageHeader
        heading="Chat Bot"
        subheading="Manage the chat bots for this application. Each bot greets users, handles unanswered questions, and can hand over to a live agent."
        variant="gradient"
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Chat Bot
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
          sx={{
            background: themeBackgrounds.default,
            color: '#ffffff',
            '&:hover': {
              background: themeBackgrounds.default,
              opacity: 0.9,
            },
          }}
        >
          Create
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, width: '100%', textAlign: 'left' }}>
        Start building your chat bot to automate replies, answer common questions, and support users in real time across your conversations.
      </Typography>

      {isLoadingBots ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading chat bots...</Typography>
        </Box>
      ) : bots.length === 0 ? (
        <Card
          sx={{
            borderRadius: 3,
            border: '1px dashed #c5d8f7',
            backgroundColor: '#f8fbff',
            py: 6,
          }}
        >
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                background: themeBackgrounds.default,
              }}
            >
              <SmartToyIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No chat bots yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first chat bot to start automating replies for this application.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
              sx={{
                background: themeBackgrounds.default,
                color: '#ffffff',
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              }}
            >
              Create
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid2 container spacing={3}>
          {bots.map((bot) => (
            <Grid2 key={bot.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                onClick={() => setSelectedBot(bot)}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 122, 255, 0.12)',
                  boxShadow: '0 8px 24px rgba(0, 122, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 16px 32px rgba(0, 122, 255, 0.16)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: themeBackgrounds.default,
                  },
                }}
              >
                <CardContent sx={{ pt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Avatar sx={{ bgcolor: bot.avatarColor, color: '#1976d2', fontWeight: 700 }}>
                        {bot.bot_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                          {bot.bot_name}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      size="small"
                      label={bot.status || 'Active'}
                      sx={{
                        background: themeBackgrounds.default,
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#007aff', mt: 0.3 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {bot.welcome_message || 'No welcome message set'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Welcome"
                      sx={{ borderColor: '#007aff', color: '#007aff' }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label="No result"
                      sx={{ borderColor: '#5fb0ff', color: '#007aff' }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Handover"
                      sx={{ borderColor: '#667eea', color: '#667eea' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      )}
    </Box>
  );
};

export default ChatBot;
